import { Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent from "components/table/TableRowComponent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useCallback, useMemo, useState } from "react";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ButtonComponent from "components/button/ButtonComponent";
import { ITireItemDetail } from "../types";
import MessageModal from "./MessageModal";

interface TableCarTireByCodeProps {
  dataTable: ITireItemDetail[];
  onRemoveItem: (id: number) => void;
  onSearch: (searchCode: string) => void;
}

// Define header data for the item table
const CAR_TIRE_HEADER = [
  {
    label: "タイヤ商品コード",
    sx: {
      width: "231px",
      textAlign: "center",
      paddingX: "8px",
    },
  },
  {
    label: "商品名",
    sx: {
      width: "385px",
      textAlign: "center",
      paddingX: "8px",
    },
  },
  {
    label: "サイズ",
    sx: {
      width: "203px",
      textAlign: "center",
      paddingX: "8px",
    },
  },
  {
    label: "荷重指数＋速度記号",
    sx: {
      width: "203px",
      textAlign: "center",
      paddingX: "8px",
    },
  },
];

const styles = {
  inputText: {
    "& input": { textAlign: "center", p: "11px", fontSize: {xs: '16px', lg: "20px"} },
    width: 185,
    height: 50,
    borderRadius: "8px",
  },
  tableCell: {
    textAlign: "center",
    background: "white",
    borderBottom: "none",
    py: "15px",
  },
  textButton: {
    fontSize: { sm: "16px", md: "20px", xl: "25px" },
  },
  tableContainer: {
    width: "max-content",
    boxShadow: "none",
  },
  removeButton: {
    fontSize: "24px",
    color: "var(--black)",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  searchButton: {
    width: 86,
  },
};

/**
 * TableCarTireByCode component renders a table with item details.
 * @param {TableCarTireByCodeProps} props - The properties for the component.
 * @param {ITireItemDetail[]} props.dataTable - The list of dataTable to display.
 * @param {Function} [props.onRemoveItem] - Optional callback function when a item is selected.
 * @returns {Element} The rendered table component.
 */
export default function TableCarTireByCode({
  dataTable,
  onRemoveItem,
  onSearch,
}: TableCarTireByCodeProps) {
  const [searchCode, setSearchCode] = useState<string>("");
  const [visibleNotification, setVisibleNotification] = useState(false);
  const [hasError, setHasError] = useState(false);

  const validateSearchCode = useCallback((code: string) => {
    if (code.length === 0 || code.length === 6) {
      return false;
    }
    return true;
  }, []);

  const onSearchByCode = useCallback(() => {
    if (!searchCode.trim()) {
      setVisibleNotification(true);
      setHasError(true);
      return;
    }

    // if (searchCode.length !== 6) {
    //   setHasError(true);
    //   return;
    // }

    onSearch(searchCode);
    setSearchCode("");
    setHasError(false);
  }, [searchCode, onSearch]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (/^\d*$/.test(value)) {
        setSearchCode(value);
        // setHasError(validateSearchCode(value));
      }
    },
    [validateSearchCode],
  );

  const handleRemoveItem = (id: number) => {
    onRemoveItem(id);
    setSearchCode("");
    setHasError(false);
  };

  const tireSizeRender = useCallback((tireItem: ITireItemDetail) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { tire_width, tire_aspect_ratio, tire_rim_diam } = tireItem;
    if (!tire_width || !tire_aspect_ratio || !tire_rim_diam) return "";
    return `${tire_width}/${tire_aspect_ratio}R${tire_rim_diam}`;
  }, []);

  const tableRows = useMemo(
    () =>
      dataTable.map((item) => (
        <TableRow key={item.id}>
          <TableCellComponent sx={styles.tableCell}>
            <TextField
              aria-readonly
              value={item.item_cd}
              variant="outlined"
              InputProps={{ sx: styles.inputText }}
            />
          </TableCellComponent>
          <TableCellComponent sx={{ ...styles.tableCell, textAlign: "left" }}>
            {item.item_name}
          </TableCellComponent>
          <TableCellComponent sx={styles.tableCell}>{tireSizeRender(item)}</TableCellComponent>
          <TableCellComponent sx={styles.tableCell}>{item.tire_loin_speed_mark}</TableCellComponent>
          <TableCellComponent sx={{...styles.tableCell, px: 0}}>
            <Button sx={styles.removeButton} onClick={() => handleRemoveItem(item.id)} variant="text" aria-label="削除">
              <RemoveCircleIcon sx={styles.removeButton} />
            </Button>
          </TableCellComponent>
        </TableRow>
      )),
    [dataTable, handleRemoveItem],
  );

  return (
    <TableContainer component={Table} sx={styles.tableContainer}>
      <TableHead>
        <TableRowComponent data={CAR_TIRE_HEADER} type="header" />
      </TableHead>
      <TableBody>
        {tableRows}

        <TableRow>
          <TableCellComponent sx={styles.tableCell}>
            <TextField
              variant="outlined"
              value={searchCode}
              onChange={handleChange}
              InputProps={{ sx: styles.inputText }}
              error={hasError}
            />
          </TableCellComponent>
          <TableCellComponent sx={{ ...styles.tableCell, textAlign: "left" }}>
            <ButtonComponent
              onClick={onSearchByCode}
              theme="dark"
              sx={{ ...styles.textButton, ...styles.searchButton }}
            >
              検索
            </ButtonComponent>
          </TableCellComponent>
        </TableRow>
      </TableBody>

      <MessageModal
        message="商品が選択されていません"
        visibleMessage={visibleNotification}
        setVisibleMessage={setVisibleNotification}
      />
    </TableContainer>
  );
}
